import { create } from 'zustand';
import * as authService from '../services/authService';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const TOKEN_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    // 1. 로그인 → 토큰 획득
    const result = await authService.login(email, password);
    localStorage.setItem(TOKEN_KEY, result.tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, result.tokens.refreshToken);

    // 2. 사용자 정보 조회
    const me = await authService.getMe();

    // 3. SUPER_ADMIN 검증
    if (me.role !== 'SUPER_ADMIN') {
      get().logout();
      throw new Error('운영 관리 권한이 없습니다.');
    }

    set({
      user: { id: me.id, email: me.email, name: me.name, role: me.role },
      isAuthenticated: true,
    });
  },

  logout: () => {
    const rt = localStorage.getItem(REFRESH_KEY);
    if (rt) {
      authService.logout(rt).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    set({ user: null, isAuthenticated: false });
  },

  checkAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const me = await authService.getMe();
      if (me.role !== 'SUPER_ADMIN') {
        get().logout();
        return;
      }
      set({
        user: { id: me.id, email: me.email, name: me.name, role: me.role },
        isAuthenticated: true,
      });
    } catch {
      get().logout();
    } finally {
      set({ isLoading: false });
    }
  },
}));
